import { useTranslation } from 'react-i18next';
import type { IExpectedVsActual } from '@interfaces/IProductivity';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { InfoTip } from '@components/ui/InfoTip/InfoTip';
import { TrendUpIcon } from '@utils/icons';
import { Card } from '../../shared/Card/Card';
import { CardHeader } from '../../shared/CardHeader/CardHeader';
import { ExpectedVsActualChart } from './ExpectedVsActualChart/ExpectedVsActualChart';
import { ProductivityEmptyState } from '../../shared/ProductivityEmptyState/ProductivityEmptyState';
import styles from './ExpectedVsActual.module.css';

interface IExpectedVsActualProps {
  data: IExpectedVsActual;
  refreshing?: boolean;
}

export function ExpectedVsActual({ data, refreshing }: IExpectedVsActualProps) {
  const { t } = useTranslation();

  // Bars carrying nothing on either series are an empty chart, not a flat one —
  // an axis with no marks says less than a sentence explaining why.
  const hasBars = data.buckets.some((b) => b.earnedWorkDays > 0 || b.actualWorkDays > 0);

  return (
    <Card refreshing={refreshing}>
      <CardHeader
        size="sm"
        icon={<TrendUpIcon size={16} />}
        title={
          <span className={styles.titleRow}>
            {t('productivity.expected.title')}
            {/* Same treatment as a column's ⓘ: meaning, never the formula. */}
            <InfoTip text={t('productivity.expected.info')} />
          </span>
        }
        meta={t(`productivity.expected.granularity.${data.granularity}`)}
      />

      {!hasBars ? (
        <ProductivityEmptyState />
      ) : (
        <>
          <ExpectedVsActualChart buckets={data.buckets} granularity={data.granularity} />

          <footer className={styles.footer}>
            <div className={styles.legend}>
              <span className={styles.legendItem}>
                <StatusDot color="var(--prod-bar-actual)" size={8} />
                {t('productivity.expected.legendActual')}
              </span>
              <span className={styles.legendItem}>
                <StatusDot color="var(--prod-bar-earned)" size={8} />
                {t('productivity.expected.legendExpected')}
              </span>
            </div>
            <span className={styles.periodAvg}>
              {t('productivity.expected.periodAvg')}:{' '}
              <strong className={styles.periodAvgValue}>{data.periodAvg}</strong>
            </span>
          </footer>
        </>
      )}
    </Card>
  );
}